import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { StudentSort } from "../types";
import * as studentsService from "../service/studentsService";
import { errorMessage } from "@/lib/notify";
import type { StudentsState, PromotePayload, ChangeSectionPayload, EditStudentPayload } from "../types";
import i18n from "@/i18n";

export const fetchStudents = createAsyncThunk(
  "students/fetchStudents",
  async ({ institutionId, ...params }: { institutionId: number; page?: number; limit?: number; academicYearId?: number; academicGroupId?: number; status?: "ACTIVE" | "INACTIVE"; search?: string; feeStatus?: "ANY_DUE" | "OVERDUE" | "PAID" | "NO_INVOICE"; minDue?: number; maxDue?: number; examId?: number; examResult?: "PASS" | "FAIL" | "PENDING"; categoryId?: number; gender?: "MALE" | "FEMALE" | "OTHER"; siblings?: "YES" | "NO"; admittedFrom?: string; admittedTo?: string; sort?: StudentSort }) => {
    return await studentsService.fetchStudents(institutionId, params);
  }
);

export const fetchStudent = createAsyncThunk("students/fetchStudent", async (studentId: number) => {
  const [student, enrollments] = await Promise.all([
    studentsService.fetchStudent(studentId),
    studentsService.fetchEnrollments(studentId),
  ]);
  return { student, enrollments };
});

/** Re-throws a failed request with the server's own message, so the toast / inline error can say what went wrong. */
function rethrow(err: unknown, fallback: string): never {
  throw new Error(errorMessage(err, fallback), { cause: err });
}

export const promoteStudent = createAsyncThunk(
  "students/promoteStudent",
  async ({ studentId, payload }: { studentId: number; payload: PromotePayload }, { dispatch }) => {
    let result;
    try {
      result = await studentsService.promoteStudent(studentId, payload);
    } catch (err) {
      rethrow(err, i18n.t("students:errors.couldNotPromoteTheStudent"));
    }
    await dispatch(fetchStudent(studentId));
    return result;
  }
);

export const changeStudentSection = createAsyncThunk(
  "students/changeStudentSection",
  async ({ studentId, payload }: { studentId: number; payload: ChangeSectionPayload }, { dispatch }) => {
    let enrollment;
    try {
      enrollment = await studentsService.changeStudentSection(studentId, payload);
    } catch (err) {
      rethrow(err, i18n.t("students:errors.couldNotChangeTheSection"));
    }
    await dispatch(fetchStudent(studentId));
    return enrollment;
  }
);

export const updateStudent = createAsyncThunk(
  "students/updateStudent",
  async ({ studentId, payload }: { studentId: number; payload: Partial<EditStudentPayload> }, { dispatch }) => {
    try {
      await studentsService.updateStudent(studentId, payload);
    } catch (err) {
      // Surface the server's own message (e.g. "Guardian phone must be a 10-digit mobile number.").
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      throw new Error(message || i18n.t("students:errors.couldNotSaveChangesPleaseTry"), { cause: err });
    }
    await dispatch(fetchStudent(studentId));
  }
);

export const withdrawStudent = createAsyncThunk(
  "students/withdrawStudent",
  async (studentId: number, { dispatch }) => {
    try {
      await studentsService.withdrawStudent(studentId);
    } catch (err) {
      rethrow(err, i18n.t("students:errors.couldNotWithdrawTheStudent"));
    }
    await dispatch(fetchStudent(studentId));
  }
);

const initialState: StudentsState = {
  students: [],
  pagination: { page: 1, limit: 20, total: 0, totalPages: 1 },
  statusCounts: { active: 0, inactive: 0 },
  selected: null,
  enrollments: [],
  loading: false,
  detailLoading: false,
  submitting: false,
  error: null,
};

const studentsSlice = createSlice({
  name: "students",
  initialState,
  reducers: {
    clearSelectedStudent(state) {
      state.selected = null;
      state.enrollments = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.loading = false;
        state.students = action.payload.students;
        state.pagination = action.payload.pagination;
        state.statusCounts = action.payload.statusCounts ?? state.statusCounts;
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? i18n.t("students:errors.failedToLoadStudents");
      })
      .addCase(fetchStudent.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchStudent.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selected = action.payload.student;
        state.enrollments = action.payload.enrollments;
      })
      .addCase(fetchStudent.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.error.message ?? i18n.t("students:errors.failedToLoadStudent");
      })
      .addCase(promoteStudent.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(promoteStudent.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(promoteStudent.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message ?? i18n.t("students:errors.failedToPromoteStudent");
      })
      .addCase(changeStudentSection.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(changeStudentSection.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(changeStudentSection.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message ?? i18n.t("students:errors.failedToChangeSection");
      })
      .addCase(updateStudent.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(updateStudent.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(updateStudent.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message ?? i18n.t("students:errors.failedToUpdateStudent");
      })
      .addCase(withdrawStudent.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(withdrawStudent.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(withdrawStudent.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message ?? i18n.t("students:errors.failedToWithdrawStudent");
      });
  },
});

export const { clearSelectedStudent } = studentsSlice.actions;
export default studentsSlice.reducer;
